import React from 'react';
import type { TFunction } from 'i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetContractDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import numberFormatter from '../../../common/numberFormatter';
import { ContractStatusObj } from '../../../common/pokCore/validation/enums';
import momencik from '../../../common/momencik';

import { NewWindowButton } from './NewWindowButton';

interface ContractSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  enableMultiSelect?: boolean;
  enableSingleSelect?: boolean;
  selectedKeys?: string[];
  showNewWindowButton?: boolean;
  initialOrderDirection?: 'ASC' | 'DESC';
  displayStatus?: boolean;
  withSignDate?: boolean;
  withUnsignDate?: boolean;
  withWaitingSignDate?: boolean;
  displayAmountUnsettled?: boolean;
}

export const contractForGrid = (
  t: TFunction<'projectlng', 'projectlng'>,
  tk: TKeys<'projectLng'>,
  contract: GetContractDto,
) => {
  return {
    key: contract.id,
    values: {
      contract_number_active: contract.systemNumber,
      'contract.number': (
        <>
          {contract.number}
          {contract.tv && (
            <OverlayTrigger placement="bottom" overlay={<Tooltip>TV</Tooltip>}>
              {contract.tv && <Icon.DisplayFill className="icon-blue ms-2" />}
            </OverlayTrigger>
          )}
        </>
      ),
      'project.number': contract.project?.number,
      'project.name': contract.project?.name,
      'client.name': contract?.client?.name,
      'responsibleOwner.name': contract.responsibleOwner?.name,
      'authorizedOwner.name': contract.authorizedOwner?.name,
      status_text: t(ContractStatusObj[contract.status]),
      contract_amount_search: numberFormatter(contract.amount, true),
      amount_unsettled: contract.amountUnsettled
        ? numberFormatter(contract.amountUnsettled, true)
        : '',
      'contract.creationTs': momencik(
        contract.creationTs,
        'YYYY-MM-DD HH:mm:ss',
      ),
      'contract.signDate': contract.signDate
        ? momencik(contract.signDate, 'YYYY-MM-DD HH:mm:ss')
        : '',
      'contract.unsignDate': contract.unsignDate
        ? momencik(contract.unsignDate, 'YYYY-MM-DD HH:mm:ss')
        : '',
      'contract.waitingSignDate': contract.waitingSignDate
        ? momencik(contract.waitingSignDate, 'YYYY-MM-DD HH:mm:ss')
        : '',
      newWindowButton: (
        <NewWindowButton contractId={contract.id} tv={contract.tv} />
      ),
    },
    isInactive: !contract.active,
    tdCssClass: {
      contract_amount_search: 'amounts-align-right',
      amount_unsettled: 'amounts-align-right',
    },
  };
};

export const ContractSearch: React.FC<ContractSearchProps> = props => {
  const { t, tk } = useTranslation('projectLng');

  const columns: GridColumn[] = [
    ...(props.showNewWindowButton
      ? [
          {
            header: '',
            property: 'newWindowButton',
            noSorting: true,
            noFilter: true,
          },
        ]
      : []),
    { header: t(tk.common.sysNumber), property: 'contract_number_active' },
    { header: t(tk.common.number), property: 'contract.number' },
    { header: t(tk.prLang.projectNumber), property: 'project.number' },
    { header: t(tk.prLang.projectName), property: 'project.name' },
    { header: t(tk.common.client), property: 'client.name' },
    ...(props.displayStatus
      ? [
          {
            header: t(tk.common.status),
            property: 'status_text',
          },
        ]
      : []),
    { header: t(tk.common.amount), property: 'contract_amount_search' },
    ...(props.displayAmountUnsettled
      ? [
          {
            header: t(tk.contract.unsettledAmount),
            property: 'amount_unsettled',
            noFilter: true,
            noSorting: true,
          },
        ]
      : []),
    {
      header: t(tk.common.responsiblePerson),
      property: 'responsibleOwner.name',
    },
    {
      header: t(tk.contract.authorizedPerson),
      property: 'authorizedOwner.name',
    },
    {
      header: t(tk.common.creationDate),
      property: 'contract.creationTs',
    },
    ...(props.withSignDate
      ? [
          {
            header: t(tk.contract.signDate),
            property: 'contract.signDate',
            noFilter: true,
          },
        ]
      : []),
    ...(props.withUnsignDate
      ? [
          {
            header: t(tk.contract.unsignDate),
            property: 'contract.unsignDate',
            noFilter: true,
          },
        ]
      : []),
    ...(props.withWaitingSignDate
      ? [
          {
            header: t(tk.contract.waitingForSigningDate),
            property: 'contract.waitingSignDate',
            noFilter: true,
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="contract_number_active"
      initialOrderDirection={props.initialOrderDirection || 'ASC'}
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.contract.noContracts)}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withHistory={true}
      withFilterInColumns={true}
      enableMultiSelect={props.enableMultiSelect}
      enableSingleSelect={props.enableSingleSelect}
      selectedKeys={props.selectedKeys}
    />
  );
};
